"use client";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  calculateMillingCosts,
  getAvailableMillingMaterials,
} from "@/lib/milling-price-calculator";
import {
  Center,
  ContactShadows,
  Environment,
  OrbitControls,
  PerspectiveCamera,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import {
  ACESFilmicToneMapping,
  Box3,
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Vector3,
} from "three";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

// @ts-expect-error: occt-import-js has no types
import initOCCT from "occt-import-js";

interface StepViewerProps {
  file: File;
  showPrice?: boolean;
}

interface StepMesh {
  attributes: {
    position: {
      array: number[];
    };
    normal?: {
      array: number[];
    };
  };
  index: {
    array: number[];
  };
  color?: [number, number, number];
}

interface StepResult {
  meshes: StepMesh[];
}

// Define type for price details state
type MillingPriceDetails = ReturnType<typeof calculateMillingCosts> | null;

export function StepViewer({ file, showPrice = false }: StepViewerProps) {
  const [model, setModel] = useState<Object3D | null>(null);
  const [unscaledModel, setUnscaledModel] = useState<Object3D | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceDetails, setPriceDetails] = useState<MillingPriceDetails>(null);
  const [selectedMaterial, setSelectedMaterial] =
    useState<string>("Aluminum 6061");

  const availableMaterials = useMemo(() => getAvailableMillingMaterials(), []);

  useEffect(() => {
    if (unscaledModel) {
      try {
        const costs = calculateMillingCosts(unscaledModel, selectedMaterial);
        setPriceDetails(costs);
      } catch (calcError) {
        console.error("Error calculating milling costs:", calcError);
        setPriceDetails(null);
      }
    } else {
      setPriceDetails(null);
    }
  }, [unscaledModel, selectedMaterial]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    setModel(null);
    setUnscaledModel(null);
    setPriceDetails(null);

    const loadStep = async () => {
      try {
        console.log("Initializing OCCT...");
        const occt = await initOCCT({
          locateFile: (filename: string) => {
            console.log("Loading WASM file:", filename);
            return `/${filename}`;
          },
        });

        console.log("OCCT initialized");

        const reader = new FileReader();
        reader.onload = async (event) => {
          if (!isMounted) return;

          try {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            const fileBuffer = new Uint8Array(arrayBuffer);

            console.log("Reading STEP file...");
            const result = occt.ReadStepFile(fileBuffer) as StepResult;

            if (!result || !result.meshes || result.meshes.length === 0) {
              throw new Error("No valid meshes found in STEP file");
            }

            console.log(`Found ${result.meshes.length} meshes in STEP file`);

            const group = new Group();
            for (const resultMesh of result.meshes) {
              const geometry = new BufferGeometry();

              geometry.setAttribute(
                "position",
                new BufferAttribute(
                  new Float32Array(resultMesh.attributes.position.array),
                  3
                )
              );

              if (resultMesh.attributes.normal) {
                geometry.setAttribute(
                  "normal",
                  new BufferAttribute(
                    new Float32Array(resultMesh.attributes.normal.array),
                    3
                  )
                );
              } else {
                geometry.computeVertexNormals();
              }

              const index = Uint16Array.from(resultMesh.index.array);
              geometry.setIndex(new BufferAttribute(index, 1));

              let material;
              if (resultMesh.color) {
                const color = new Color(
                  resultMesh.color[0],
                  resultMesh.color[1],
                  resultMesh.color[2]
                );
                console.log("Color:", color);
                material = new MeshStandardMaterial({
                  color,
                  metalness: 0.15,
                  roughness: 0.5,
                });
              } else {
                material = new MeshStandardMaterial({
                  color: "#ffb95a",
                  metalness: 0.15,
                  roughness: 0.5,
                });
              }

              const mesh = new Mesh(geometry, material);
              group.add(mesh);
            }

            const calculatedUnscaledModel = group.clone();
            if (isMounted) {
              setUnscaledModel(calculatedUnscaledModel);
            }

            const bbox = new Box3().setFromObject(group);
            const center = new Vector3();
            bbox.getCenter(center);
            group.position.copy(center.multiplyScalar(-1));

            const size = new Vector3();
            bbox.getSize(size);
            const maxDim = Math.max(size.x, size.y, size.z);
            if (maxDim > 0) {
              const scale = 2 / maxDim;
              group.scale.setScalar(scale);
            }

            if (isMounted) {
              console.log("Setting model...");
              setModel(group);
            }
          } catch (err: unknown) {
            console.error("Error processing STEP file:", err);
            if (err instanceof Error) {
              setError(err.message || "Failed to load STEP file");
            } else {
              setError("Failed to load STEP file");
            }
          } finally {
            setLoading(false);
          }
        };
        reader.onerror = () => {
          setError("FileReader error");
          setLoading(false);
        };
        reader.readAsArrayBuffer(file);
      } catch (err: unknown) {
        console.error("Error initializing OCCT:", err);
        if (err instanceof Error) {
          setError(err.message || "Failed to initialize OCCT");
        } else {
          setError("Failed to initialize OCCT");
        }
        setLoading(false);
      }
    };
    loadStep();
    return () => {
      isMounted = false;
      unscaledModel?.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
        }
      });
      model?.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
        }
      });
    };
  }, [file]);

  return (
    <div className="space-y-4">
      <div className="w-full h-[400px] bg-muted/50 rounded-lg overflow-hidden relative border shadow-sm">
        <Canvas
          className="absolute inset-0"
          gl={{
            antialias: true,
            toneMapping: ACESFilmicToneMapping,
            powerPreference: "high-performance",
            alpha: true,
          }}
          shadows
          dpr={[1, 2]}
        >
          <color attach="background" args={["#f0f0f0"]} />
          <Environment preset="apartment" />
          <PerspectiveCamera
            makeDefault
            fov={45}
            position={[0, 1, 2.5]}
            near={0.1}
            far={1000}
          />
          <ambientLight intensity={0.15} />
          <hemisphereLight
            color={0xffffff}
            groundColor={0x444444}
            intensity={0.6}
          />
          <directionalLight
            color={0xffffff}
            position={[3, 3, 3]}
            intensity={1.2}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <directionalLight
            color={0xccccff}
            position={[-1, 0.75, -0.5]}
            intensity={0.4}
          />
          <gridHelper
            args={[10, 20, "#111111", "#080808"]}
            position={[0, -0.01, 0]}
          />
          <ContactShadows
            frames={1}
            position={[0, -0.01, 0]}
            blur={2}
            opacity={0.4}
            far={1}
          />
          {model && (
            <Center>
              <primitive object={model} />
            </Center>
          )}
          <OrbitControls
            enableDamping
            dampingFactor={0.05}
            rotateSpeed={0.5}
            zoomSpeed={0.7}
            panSpeed={0.5}
            minDistance={0.1}
            maxDistance={10}
            target={[0, 0, 0]}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 2.1}
          />
        </Canvas>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <span className="text-muted-foreground">Loading STEP file...</span>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-100/80 z-10">
            <span className="text-destructive">{error}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 border rounded-md bg-muted/20">
        <div>
          <Label htmlFor="step-material-select" className="mb-2">
            Material
          </Label>
          <Select
            value={selectedMaterial}
            onValueChange={setSelectedMaterial}
            name="step-material-select"
          >
            <SelectTrigger id="step-material-select">
              <SelectValue placeholder="Select material" />
            </SelectTrigger>
            <SelectContent>
              {availableMaterials.map((material) => (
                <SelectItem key={material} value={material}>
                  {material}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showPrice && priceDetails && (
        <Card>
          <CardHeader>
            <CardTitle>Milling Price Estimate ({selectedMaterial})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Stock Volume (BBox):
              </span>
              <span>{priceDetails.bboxVolume.toFixed(2)} cm³</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Part Volume:</span>
              <span>{priceDetails.partVolume.toFixed(2)} cm³</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material Removed:</span>
              <span>{priceDetails.removalVolume.toFixed(2)} cm³</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Complexity (Triangles):
              </span>
              <span>{priceDetails.triangleCount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material Cost:</span>
              <span>${priceDetails.materialCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. Machine Time:</span>
              <span>{priceDetails.machineTime.toFixed(1)} hours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Setup Cost:</span>
              <span>${priceDetails.setupCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t pt-2 mt-2">
              <span>Total Estimated Cost:</span>
              <span>${priceDetails.totalCost.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}
      {showPrice && !priceDetails && unscaledModel && !loading && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {error
                ? "Error calculating price."
                : `Calculating price for ${selectedMaterial}...`}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
